"use client";

import type {
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  ReactNode,
} from "react";
import { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslations } from "use-intl";

import { CloseIcon, OpenExternalIcon, StoreIcon } from "@/components/appIcons";
import type { DealOffer } from "@/components/marketplace/deal-offers-types";
import { useHashModal } from "@/hooks/use-hash-modal";
import { useRastrPurchaseHref } from "@/hooks/useRastrPurchaseHref";
import { trackOfferClicked } from "@/lib/analytics";
import { cn } from "@/lib/utils";
export function DealOfferLoadingSkeleton() {
  return (
    <div className="rounded-xl border border-border bg-card/70 p-3">
      <div className="flex animate-pulse items-center gap-3">
        <div className="h-12 w-12 rounded-lg bg-muted" />
        <div className="min-w-0 flex-1 space-y-2">
          <div className="h-3 w-24 rounded bg-muted" />
          <div className="h-4 w-44 rounded bg-muted" />
        </div>
        <div className="h-8 w-24 rounded bg-muted" />
      </div>
    </div>
  );
}

export function BrandIcon({
  iconUrl,
  className,
  imgClassName,
}: {
  iconUrl?: string | null;
  className?: string;
  imgClassName?: string;
}) {
  if (!iconUrl) return null;

  return (
    <span
      className={cn(
        "inline-flex h-8 w-8 items-center justify-center border border-border bg-background",
        className,
      )}
    >
      <img
        src={iconUrl}
        alt=""
        className={cn("h-6 w-6 object-contain", imgClassName)}
        loading="lazy"
      />
    </span>
  );
}

type OfferActionCtaVariant = "outline" | "solid" | "compact";

const OFFER_ACTION_CTA_STYLES: Record<
  OfferActionCtaVariant,
  { root: string; label: string; secondary: string; icon: string; iconBox: string }
> = {
  outline: {
    root: "inline-flex min-h-12 items-center justify-start gap-2 rounded-lg border border-orange-500 bg-transparent px-3 py-1.5 text-left text-orange-600 transition-colors hover:bg-orange-500/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background dark:text-orange-400",
    label: "text-sm font-black",
    secondary: "text-orange-600/70 dark:text-orange-400/70",
    icon: "text-orange-600/65 dark:text-orange-400/65",
    iconBox: "size-7",
  },
  solid: {
    root: "inline-flex min-h-13 items-center justify-start gap-3 rounded-lg border border-primary bg-primary py-2 pl-2 pr-4 text-left text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background",
    label: "text-[15px] font-bold",
    secondary: "text-primary-foreground/75",
    icon: "text-lg text-primary-foreground",
    iconBox: "size-7",
  },
  /** Offer-row store link: fills the fixed store column so every row's button matches. */
  compact: {
    root: "inline-flex min-h-11 max-w-full items-center justify-start gap-1.5 rounded-lg border border-orange-500/70 bg-transparent px-2 text-left text-orange-600 transition-colors hover:bg-orange-500/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background dark:text-orange-400 lg:min-h-9",
    label: "text-[13px] font-bold",
    secondary: "text-orange-600/70 dark:text-orange-400/70",
    icon: "text-sm text-orange-600/65 dark:text-orange-400/65",
    iconBox: "size-5",
  },
};

export function OfferActionCta({
  href,
  actionLabel,
  ariaLabel,
  secondaryLabel,
  providerIconUrl,
  pendingLabel,
  variant = "outline",
  className,
}: {
  href: string | null;
  actionLabel: string;
  ariaLabel: string;
  secondaryLabel?: string;
  providerIconUrl?: string | null;
  pendingLabel: string;
  variant?: OfferActionCtaVariant;
  className?: string;
}) {
  const styles = OFFER_ACTION_CTA_STYLES[variant];
  if (!href) {
    return (
      <span
        className={cn(
          "inline-flex min-h-10 items-center justify-center rounded-lg border border-border px-4 text-sm font-bold text-muted-foreground",
          className,
        )}
      >
        {pendingLabel}
      </span>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener sponsored"
      aria-label={ariaLabel}
      onClick={trackOfferClicked}
      className={cn(styles.root, className)}
    >
      <span className={cn("inline-flex shrink-0 items-center justify-center", styles.iconBox)}>
        {providerIconUrl ? (
          <img
            src={providerIconUrl}
            alt=""
            className={cn("rounded-md object-contain", styles.iconBox)}
            loading="lazy"
          />
        ) : (
          <StoreIcon className="text-base" />
        )}
      </span>
      <span className="flex min-w-0 flex-1 flex-col items-start justify-center gap-0.5">
        <span className={cn("block w-full truncate leading-none", styles.label)}>
          {actionLabel}
        </span>
        {secondaryLabel && (
          <span
            className={cn(
              "max-w-full truncate text-[10px] font-semibold leading-none",
              styles.secondary,
            )}
          >
            {secondaryLabel}
          </span>
        )}
      </span>
      <OpenExternalIcon
        aria-hidden="true"
        className={cn("ml-auto shrink-0 text-base", styles.icon)}
      />
    </a>
  );
}

export function DealOfferActionCta({
  offer,
  providerLabelOnly = false,
  actionLabelOverride,
  variant,
  className,
}: {
  offer: DealOffer;
  providerLabelOnly?: boolean;
  /** Replaces the visible and accessible action text, for example "Buy at {store}". */
  actionLabelOverride?: string;
  variant?: OfferActionCtaVariant;
  className?: string;
}) {
  const t = useTranslations("shared.affiliates");
  const label = actionLabelOverride ?? `${t("viewOffer")} · ${offer.providerDisplayName}`;
  const actionLabel =
    actionLabelOverride ?? (providerLabelOnly ? offer.providerDisplayName : t("viewOffer"));
  const href = useRastrPurchaseHref(offer.linkType ? offer.purchaseUrl : null);

  return (
    <OfferActionCta
      href={href}
      actionLabel={actionLabel}
      ariaLabel={label}
      secondaryLabel={
        providerLabelOnly || actionLabelOverride ? undefined : offer.providerDisplayName
      }
      providerIconUrl={offer.providerIconUrl}
      pendingLabel={t("linkPending")}
      variant={variant}
      className={className}
    />
  );
}

type ModalShellChildren<TPayload> =
  | ReactNode
  | ((args: { close: () => void; payload: TPayload | null }) => ReactNode);

export function ModalShell<TPayload = unknown>({
  title,
  children,
  onClose,
  widthClassName = "md:max-w-3xl",
  modal,
  ownerId,
}: {
  title: string;
  children: ModalShellChildren<TPayload>;
  onClose?: () => void;
  widthClassName?: string;
  modal: string;
  ownerId?: string;
}) {
  const t = useTranslations("shared.affiliates");
  const hashModal = useHashModal();
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const modalOptions = ownerId ? { ownerId } : undefined;
  const state = hashModal.getState<TPayload>(modal, modalOptions);
  const open = state.open;
  const handleClose = () => {
    hashModal.close(modal, modalOptions);
    onClose?.();
  };

  useEffect(() => {
    if (!open || typeof document === "undefined") return;

    previousFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const frame = window.requestAnimationFrame(() => {
      (closeButtonRef.current ?? panelRef.current)?.focus();
    });

    return () => {
      window.cancelAnimationFrame(frame);
      document.body.style.overflow = previousBodyOverflow;
      const previousFocus = previousFocusRef.current;
      previousFocusRef.current = null;
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [open]);

  if (typeof document === "undefined" || !open) return null;

  const content =
    typeof children === "function"
      ? children({ close: handleClose, payload: state.payload })
      : children;

  if (content == null || content === false) return null;

  const handleBackdropClick = (event: ReactMouseEvent) => {
    if (event.target === event.currentTarget) handleClose();
  };
  const handleKeyDown = (event: ReactKeyboardEvent) => {
    if (event.key === "Escape") {
      event.preventDefault();
      handleClose();
      return;
    }

    if (event.key !== "Tab") return;
    const panel = panelRef.current;
    if (!panel) return;
    const focusable = Array.from(
      panel.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ).filter((element) => !element.hasAttribute("disabled") && element.offsetParent !== null);
    if (focusable.length === 0) {
      event.preventDefault();
      panel.focus();
      return;
    }

    const first = focusable[0]!;
    const last = focusable.at(-1)!;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex bg-black/60 md:items-center md:justify-center"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        className={cn(
          "flex h-dvh min-h-0 max-h-dvh w-full flex-col overflow-hidden overscroll-none bg-card md:mx-4 md:h-auto md:max-h-[85vh] md:rounded-2xl md:border md:border-border md:shadow-2xl",
          widthClassName,
        )}
      >
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border px-4 py-3 sm:px-5 md:px-6">
          <h3 id={titleId} className="text-lg font-black">
            {title}
          </h3>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={handleClose}
            className="inline-flex h-11 w-11 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:text-foreground lg:h-9 lg:w-9"
            aria-label={t("close")}
          >
            <CloseIcon className="text-lg" />
          </button>
        </div>
        <div className="min-h-0 flex-1 touch-pan-y overflow-y-auto overscroll-contain">
          {content}
        </div>
      </div>
    </div>,
    document.body,
  );
}
